
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2 } from 'lucide-react';
import { apiClient } from '@/services/apiClient';
import { useCreateIntegration, useUpdateIntegration } from '@/hooks/useApiIntegrations';
import { ApiProvider } from '@/hooks/useApiProviders';

interface ConnectForm24SevenProps {
  provider: ApiProvider;
  userId: string;
  onSuccess?: () => void;
}

const formSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
  applicationId: z.string().min(1, 'Application ID is required'),
  identityId: z.string().min(1, 'Identity/Community ID is required'),
});

export function ConnectForm24Seven({ provider, userId, onSuccess }: ConnectForm24SevenProps) {
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  
  const { mutate: createIntegration } = useCreateIntegration();
  const { mutate: updateIntegration } = useUpdateIntegration();
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: '',
      password: '',
      applicationId: '',
      identityId: '',
    },
  });
  
  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    setIsAuthenticating(true);
    setError(null);
    
    try {
      // Get token from edge function
      const tokenResult = await apiClient.getToken({
        providerId: provider.id,
        credentials: {
          username: data.username,
          password: data.password,
          applicationId: data.applicationId,
          identityId: data.identityId,
        },
      });
      
      if (!tokenResult.access_token) {
        throw new Error('No access token received');
      }
      
      // Calculate token expiry
      const expiresAt = new Date();
      expiresAt.setSeconds(expiresAt.getSeconds() + (tokenResult.expires_in || 3600));
      
      const integrationData = {
        user_id: userId,
        provider_id: provider.id,
        access_token: tokenResult.access_token,
        refresh_token: tokenResult.refresh_token,
        token_expires_at: expiresAt.toISOString(),
        credentials: {
          applicationId: data.applicationId,
          identityId: data.identityId,
          // Don't store password
        },
        connection_status: 'connected',
      };
      
      // Save to database
      createIntegration(integrationData, {
        onSuccess: () => {
          if (onSuccess) onSuccess();
        },
        onError: (err: any) => {
          setError(`Failed to save integration: ${err.message}`);
        }
      });
    } catch (err: any) {
      setError(`Authentication failed: ${err.message}`);
    } finally {
      setIsAuthenticating(false);
    }
  };
  
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 py-4">
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Username</FormLabel>
                <FormControl>
                  <Input placeholder="24Seven username" type="text" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <Input placeholder="24Seven password" type="password" {...field} />
                </FormControl>
                <FormDescription>
                  Your password is not stored in our database
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          <FormField
            control={form.control}
            name="applicationId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Application ID</FormLabel>
                <FormControl>
                  <Input placeholder="24Seven Application ID" {...field} />
                </FormControl>
                <FormDescription>
                  Found in your 24Seven Office API settings
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="identityId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Identity/Community ID</FormLabel>
                <FormControl>
                  <Input placeholder="24Seven Identity ID" {...field} />
                </FormControl>
                <FormDescription>
                  Also known as Community ID
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <div className="flex justify-end mt-4">
          <Button type="submit" disabled={isAuthenticating}>
            {isAuthenticating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Connect
          </Button>
        </div>
      </form>
    </Form>
  );
}
